import { Dispatch, SetStateAction, useEffect } from 'react';
import { ImageOverlay, ProductImage } from '~/shared/components/Image';
import { BigImage, ImageButton, ImageList, ImageListItem, Wrapper } from './styled';
import { useProductImages } from '~/services/shop/hooks/selectors';

interface Props {
    imageIndex: number;
    setImageIndex: Dispatch<SetStateAction<number>>;
}

export const ProductGalleryModal = ({ imageIndex, setImageIndex }: Props) => {
    const { data: images } = useProductImages();

    useEffect(() => {
        function handleKeyPress(event: KeyboardEvent) {
            const firstImage = 0;
            const lastImage = images!.length - 1;
            const prevImage = Math.max(imageIndex - 1, firstImage);
            const nextImage = Math.min(imageIndex + 1, lastImage);

            switch (event.key) {
                case 'PageUp': {
                    setImageIndex(firstImage);
                    break;
                }

                case 'PageDown': {
                    setImageIndex(lastImage);
                    break;
                }

                case 'ArrowDown':
                case 'ArrowRight': {
                    setImageIndex(nextImage);
                    break;
                }

                case 'ArrowUp':
                case 'ArrowLeft': {
                    setImageIndex(prevImage);
                }
            }
        }

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [images, imageIndex, setImageIndex]);

    const filteredImages = images?.filter((image) => !!image.link);

    if (filteredImages?.length) {
        const currentImage =
            filteredImages[imageIndex] ?? filteredImages[filteredImages.length - 1];

        return (
            <Wrapper oneImage={filteredImages.length === 1}>
                {filteredImages.length > 1 && (
                    <ImageList>
                        {filteredImages.map((image, index) => (
                            <ImageListItem key={`img-modal-${image.link}-${index}`}>
                                <ImageButton
                                    isActiveMedia={index === imageIndex}
                                    onClick={() => setImageIndex(index)}
                                >
                                    <ProductImage
                                        alt={image.displayName ?? ''}
                                        fill
                                        sizes="70px"
                                        style={{ objectFit: 'cover', objectPosition: 'center' }}
                                        src={image.link!}
                                    />
                                    <ImageOverlay />
                                </ImageButton>
                            </ImageListItem>
                        ))}
                    </ImageList>
                )}

                <BigImage>
                    <ProductImage
                        alt={currentImage.displayName ?? ''}
                        fill
                        sizes="(min-width: 768px) 100vw"
                        style={{ objectFit: 'contain', objectPosition: 'center' }}
                        src={currentImage.link!}
                    />
                    <ImageOverlay />
                </BigImage>
            </Wrapper>
        );
    }

    return null;
};
